import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Loader2, ShieldAlert } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Fingerprint } from '../../components/cultivar/contracts/ContractBits';
import upstreamService from '../../services/upstream';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { downloadBlob } from '../../utils/downloadBlob';
import { money } from '../../utils/nurseryMoney';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import type { UpstreamAccrualKind, UpstreamStatement } from '../../types/upstream';

/** /cultivar/upstream/statements/:id — one month's frozen statement of what is owed upstream. */

const KIND: Record<UpstreamAccrualKind, string> = { tree_sold: 'Sold', tree_returned: 'Back', annual: 'Yearly' };

function UpstreamStatementView() {
  const { id } = useParams<{ id: string }>();
  const tz = useOrgTimezone();
  const cultivarSeat = useModuleLicense('cultivar', ['pbr', 'admin']);
  const accountsSeat = useModuleLicense('accounts', ['user', 'admin']);
  const cultivarAdmin = useModuleLicense('cultivar', 'admin');
  const accountsAdmin = useModuleLicense('accounts', 'admin');
  const canRead = cultivarSeat.hasAccess || accountsSeat.hasAccess;
  const canAdmin = cultivarAdmin.hasAccess || accountsAdmin.hasAccess;

  const [st, setSt] = useState<UpstreamStatement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paidAt, setPaidAt] = useState('');
  const [paidRef, setPaidRef] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await upstreamService.getStatement(Number(id));
      if (res.success && res.data) {
        setSt(res.data);
        setPaidAt(res.data.paid_at ? res.data.paid_at.slice(0, 10) : '');
        setPaidRef(res.data.paid_reference || '');
      } else setError(errorMessage(res, 'The statement could not be read'));
    } catch (e) {
      setError(errorMessage(e, 'The statement could not be read'));
    }
  }, [id]);

  useEffect(() => { if (canRead) load(); }, [canRead, load]);

  const savePaid = async (clear = false) => {
    if (!st) return;
    setBusy(true); setError(null);
    try {
      const res = await upstreamService.markPaid(st.id, clear ? null : paidAt || null, clear ? null : paidRef);
      if (res.success && res.data) setSt(res.data); else setError(errorMessage(res, 'Could not be saved'));
    } catch (e) {
      setError(errorMessage(e, 'Could not be saved'));
    }
    setBusy(false);
  };

  const csv = async () => {
    if (!st) return;
    try {
      downloadBlob(await upstreamService.statementCsv(st.id), `upstream-${st.payee_name}-${st.period_to.slice(0, 7)}.csv`.replace(/[^\w.-]+/g, '-'));
    } catch (e) {
      setError(errorMessage(e, 'The CSV could not be downloaded'));
    }
  };

  if (cultivarSeat.loading || accountsSeat.loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-neutral-400" /></div>;
  if (!canRead) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card><CardContent className="p-6 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
          <p className="text-sm text-gray-600">A Cultivar PBR or Admin seat, or an Accounts seat, is needed to read this.</p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <>
      <PageHeader title={st ? `Owed to ${st.payee_name}` : 'Upstream statement'}
        subtitle={st ? `${formatLocalDateInTz(st.period_from.slice(0, 10), tz)} to ${formatLocalDateInTz(st.period_to.slice(0, 10), tz)}` : undefined}
        icon={<FileText className="h-6 w-6 text-white" />} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <Link to="/cultivar/upstream" className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Owed upstream
        </Link>
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
        {st && (
          <>
            <Card>
              <CardContent className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><div className="text-neutral-500">Trees sold</div><div className="text-lg font-semibold">{st.trees_sold}</div></div>
                <div><div className="text-neutral-500">Trees licensed (yearly)</div><div className="text-lg font-semibold">{st.trees_annual}</div></div>
                <div><div className="text-neutral-500">Owed</div><div className="text-lg font-semibold">{money(st.amount)}</div></div>
                <div><div className="text-neutral-500">You collected</div><div className="text-lg font-semibold">{money(st.collected)}</div></div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>By cultivar</CardTitle></CardHeader>
              <CardContent className="space-y-1 text-sm">
                {st.by_cultivar.map((c) => (
                  <div key={c.cultivar_name} className="flex flex-wrap justify-between gap-x-3 border-b border-neutral-100 py-1">
                    <span className="font-medium break-words min-w-0">{c.cultivar_name}</span>
                    <span className="text-neutral-700">{c.trees_sold} sold{c.trees_annual ? `, ${c.trees_annual} licensed` : ''} · owed {money(c.amount)} · collected {money(c.collected)}</span>
                  </div>
                ))}
                <Fingerprint hash={st.content_hash} label="Fingerprint of this statement" />
                <div className="pt-2"><Button variant="outline" size="sm" onClick={csv}>Download CSV</Button></div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Paid</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                {st.paid_at
                  ? <p className="text-green-800">Paid {formatLocalDateInTz(st.paid_at.slice(0, 10), tz)}{st.paid_reference ? `, ${st.paid_reference}` : ''}.</p>
                  : <p className="text-amber-800">Not marked paid.</p>}
                {canAdmin && (
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="space-y-1">
                      <label htmlFor="up-paid-at" className="block text-xs text-neutral-600">Paid on</label>
                      <input id="up-paid-at" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className="rounded-lg border border-neutral-300 px-3 py-2 text-sm" />
                    </div>
                    <div className="space-y-1 min-w-0">
                      <label htmlFor="up-paid-ref" className="block text-xs text-neutral-600">Reference</label>
                      <input id="up-paid-ref" value={paidRef} onChange={(e) => setPaidRef(e.target.value)} maxLength={120} className="rounded-lg border border-neutral-300 px-3 py-2 text-sm w-40" />
                    </div>
                    <Button size="sm" onClick={() => savePaid()} disabled={busy || !paidAt}>Save</Button>
                    {st.paid_at && <Button variant="outline" size="sm" onClick={() => savePaid(true)} disabled={busy}>Not paid after all</Button>}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Every tree</CardTitle></CardHeader>
              <CardContent>
                <ul className="divide-y divide-neutral-100 text-sm">
                  {st.lines.map((l) => (
                    <li key={l.id} className="py-1.5 flex flex-wrap justify-between gap-x-3">
                      <span className="break-words min-w-0">
                        {formatLocalDateInTz(l.event_date.slice(0, 10), tz)} · {KIND[l.kind]} · {l.cultivar_name}
                        {l.document_number ? ` · ${l.document_number}` : ''}{l.customer_name ? ` · ${l.customer_name}` : ''}
                      </span>
                      <span className="text-neutral-700 whitespace-nowrap">{l.quantity} × ${l.rate} = {money(l.amount)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </>
  );
}

export default UpstreamStatementView;
